import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, AlertTriangle, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import {
  getRegisterCatalogue,
  previewRegister,
  downloadRegister
} from '../../services/api/implementation/impl';
import {
  Callout
} from '../../../shared/components/ui/primitives';

/**
 * Statutory registers under the Contract Labour (R&A) Act.
 *
 * Everything here is generated from the muster and the wage ledger — there is
 * no data entry. Preview first, then print: the preview exists so somebody sees
 * what the form will say before it is signed and filed.
 */
const LabourRegisters = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const [form, setForm] = useState('XIII');
  const [from, setFrom] = useState(() => firstOfMonth());
  const [to, setTo] = useState(() => today());
  const [preview, setPreview] = useState(null);

  const catalogue = useQuery({
    queryKey: ['registerCatalogue'],
    queryFn: getRegisterCatalogue
  });

  const load = useMutation({
    mutationFn: () => previewRegister(form, { from, to }),
    onSuccess: setPreview,
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not build the register')
  });

  const download = useMutation({
    mutationFn: () => downloadRegister(form, { from, to }),
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not generate the PDF')
  });

  const registers = catalogue.data ?? [];
  const selected = registers.find((r) => r.form === form);

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Statutory Registers' }} />

      <div className="p-4 space-y-4">
        <Callout tone="info" collapsible title="These are the Central Rules forms">
          Several states publish their own variants with different columns — have a
          sample checked against your state&apos;s rules before treating the output
          as filing-ready.
        </Callout>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Register
              </label>
              <select
                value={form}
                onChange={(e) => {
                  setForm(e.target.value);
                  setPreview(null);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                {registers.map((r) => (
                  <option key={r.form} value={r.form}>
                    Form {r.form} — {r.name}
                  </option>
                ))}
              </select>
            </div>

            <DateField label="From" value={from} onChange={setFrom} />
            <DateField label="To" value={to} onChange={setTo} />
          </div>

          {selected && (
            <p className="text-xs text-gray-500 mt-2">{selected.description}</p>
          )}

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              disabled={load.isPending}
              onClick={() => load.mutate()}
              className="px-4 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium disabled:opacity-50"
            >
              {load.isPending ? 'Building…' : 'Preview'}
            </button>
            <button
              type="button"
              disabled={download.isPending}
              onClick={() => download.mutate()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50"
            >
              <Download size={15} />
              {download.isPending ? 'Generating…' : 'Download PDF'}
            </button>
          </div>
        </div>

        {load.isPending ? (
          <Loader />
        ) : preview ? (
          <>
            {preview.incomplete?.length > 0 && (
              <div className="flex items-start gap-2 rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <strong>
                    {preview.incomplete.length}{' '}
                    {preview.incomplete.length === 1 ? 'workman' : 'workmen'} in this period{' '}
                    {preview.incomplete.length === 1 ? 'is' : 'are'} missing
                    particulars this register requires.
                  </strong>
                  <p className="text-xs mt-1">
                    They are printed on the register too, so whoever signs it can
                    see the gap. Complete them from the labour register.
                  </p>
                  <div className="mt-2 text-xs space-y-0.5">
                    {preview.incomplete.slice(0, 6).map((w) => (
                      <div key={w.worker_id}>
                        <span className="font-medium">{w.name}</span> —{' '}
                        {w.missing.join(', ')}
                      </div>
                    ))}
                    {preview.incomplete.length > 6 && (
                      <div className="italic">
                        …and {preview.incomplete.length - 6} more.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="font-medium text-gray-900">
                  Form {preview.form} — {preview.name}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {preview.context?.companyName} · {preview.context?.from} to{' '}
                  {preview.context?.to} · {preview.rows?.length ?? 0} row
                  {preview.rows?.length === 1 ? '' : 's'}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      {preview.columns.map((c) => (
                        <th
                          key={c.key}
                          className="text-left font-medium px-3 py-2 whitespace-nowrap"
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.rows ?? []).length === 0 && (
                      <tr>
                        <td
                          colSpan={preview.columns.length}
                          className="text-center py-10 text-gray-500"
                        >
                          No entries for this period.
                        </td>
                      </tr>
                    )}
                    {(preview.rows ?? []).map((row, index) => (
                      <tr key={index} className="border-t border-gray-100">
                        {preview.columns.map((c) => (
                          <td key={c.key} className="px-3 py-2 align-top">
                            {formatCell(row[c.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg py-10 text-center text-gray-500">
            <FileText size={22} className="mx-auto mb-2 text-gray-400" />
            Choose a register and a period, then preview it.
          </div>
        )}
      </div>
    </Layout>
  );
};

const DateField = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
    />
  </div>
);

function formatCell(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-01`;
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
}

export default LabourRegisters;
